import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Package,
  Calendar,
  MapPin,
  Clock,
  CheckCircle,
  AlertTriangle,
  Search,
  Filter,
  Download,
  Plus,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  History,
  ArrowRight,
  ArrowLeft,
  FileText,
  Building,
  User,
  Mail,
  Phone,
  X,
  XCircle,
  Save,
  RefreshCw,
  HelpCircle,
  MoreHorizontal
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';
import AssignmentService from '../../services/assignmentService';
import { useToast, ToastContainer } from '../common/Toast';
import AssignmentTestService from '../../services/assignmentTestService';
import TestResultsModal from '../common/TestResultsModal';
import TestControlPanel from '../common/TestControlPanel';
import AssignmentTestAutomation from '../../services/assignmentTestAutomation';
import { validateAssignmentForm, validateField, getValidationClass, sanitizeFormData } from '../../utils/assignmentValidation';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { assetService } from '../../services/assetService';
import UserService from '../../services/userService';

// Project-style Action Dropdown (Assignment Usage)
const ActionDropdown = ({ assignment, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setOpenUpward(window.innerHeight - rect.bottom < 120);
    }
    setIsOpen(prev => !prev);
  };

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 10000 : 'auto' }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            transform: 'translateX(-75%)'
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                onClick={() => { setIsOpen(false); onView(assignment); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View
              </button>
            </li>

            <li>
              <button
                onClick={() => { setIsOpen(false); onEdit(assignment); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit
              </button>
            </li>

            <li className="border-t">
              <button
                onClick={() => { setIsOpen(false); onDelete(assignment); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
              >
                <Trash2 size={16} className="mr-2.5 text-red-500" />
                Delete
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const AssignmentUsage = () => {
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const { toasts, success, error: showError, removeToast } = useToast();

  // Test-related state
  const [showTestResults, setShowTestResults] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [showTestControlPanel, setShowTestControlPanel] = useState(false);
  const [automationTestResults, setAutomationTestResults] = useState(null);
  const isDevelopmentMode = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

  // Form ref for test automation
  const formRef = useRef(null);

  // Validation state
  const [formErrors, setFormErrors] = useState({});
  const [formWarnings, setFormWarnings] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // History state
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [historyFilters, setHistoryFilters] = useState({
    dateRange: 'all',
    actionType: 'all',
    user: 'all',
    department: 'all'
  });

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState({
    departmentStats: [],
    assetTypeStats: [],
    utilizationTrends: [],
    topUsers: [],
    overdueAnalysis: [],
    monthlyTrends: []
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Real assignments data from backend
  const [assignments, setAssignments] = useState([]);

  // Fetch assignments data from backend
  const fetchAssignments = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Auto-update overdue statuses before fetching
      try {
        await AssignmentService.checkOverdue();
      } catch (e) {
        console.warn('Failed to auto-check overdue assignments:', e);
      }

      const response = await AssignmentService.getAssignments({
        search: searchTerm,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        department: filterDepartment !== 'all' ? filterDepartment : undefined
      });

      setAssignments(response.data || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError(error.message || 'Failed to fetch assignments');
      showError('Failed to load assignments data');

      // Fallback to mock data if API fails
      setAssignments([
        {
          id: 'ASG-001',
          assetId: 'AST-007',
          assetName: 'Dell Latitude 5520',
          assetType: 'Laptop',
          assignedTo: 'John Boy',
          assignedToId: 'EMP-001',
          assignedToEmail: 'john.boy@company.com',
          assignedToPhone: '+1-555-0123',
          department: 'IT',
          assignedDate: '2024-01-15',
          expectedReturnDate: '2024-12-31',
          returnDate: null,
          status: 'Active',
          location: 'Office Floor 2, Desk 15',
          purpose: 'Development Work',
          condition: 'Good',
          notes: 'Primary development machine for React projects',
          assignedBy: 'IT Manager',
          lastUpdated: '2024-01-15T10:30:00Z'
        },
        {
          id: 'ASG-002',
          assetId: 'AST-005',
          assetName: 'Warehouse Facility',
          assetType: 'Real Estate',
          assignedTo: 'Pradip Chandupatla',
          assignedToId: 'EMP-002',
          assignedToEmail: 'pradip.chandupatla@company.com',
          assignedToPhone: '+1-555-0124',
          department: 'Operations',
          assignedDate: '2024-01-10',
          expectedReturnDate: null,
          returnDate: null,
          status: 'Active',
          location: 'Industrial Zone, Building A',
          purpose: 'Storage Operations',
          condition: 'Excellent',
          notes: 'Main warehouse for inventory management',
          assignedBy: 'Operations Manager',
          lastUpdated: '2024-01-10T09:15:00Z'
        },
        {
          id: 'ASG-003',
          assetId: 'AST-004',
          assetName: 'IT Equipment - Server Rack',
          assetType: 'Server',
          assignedTo: 'Rahul Gupta',
          assignedToId: 'EMP-003',
          assignedToEmail: 'rahul.gupta@company.com',
          assignedToPhone: '+1-555-0125',
          department: 'IT',
          assignedDate: '2024-01-20',
          expectedReturnDate: '2025-01-20',
          returnDate: null,
          status: 'Active',
          location: 'Data Center, Rack 12',
          purpose: 'Server Management',
          condition: 'Good',
          notes: 'Production server for web applications',
          assignedBy: 'IT Manager',
          lastUpdated: '2024-01-20T14:45:00Z'
        },
        {
          id: 'ASG-004',
          assetId: 'AST-003',
          assetName: 'Office Furniture Set',
          assetType: 'Furniture',
          assignedTo: 'Sarah Johnson',
          assignedToId: 'EMP-004',
          assignedToEmail: 'sarah.johnson@company.com',
          assignedToPhone: '+1-555-0126',
          department: 'HR',
          assignedDate: '2024-01-05',
          expectedReturnDate: '2024-01-25',
          returnDate: '2024-01-25',
          status: 'Returned',
          location: 'HR Department, Office 3',
          purpose: 'Temporary Assignment',
          condition: 'Good',
          notes: 'Temporary furniture for new employee onboarding',
          assignedBy: 'HR Manager',
          lastUpdated: '2024-01-25T16:20:00Z'
        },
        {
          id: 'ASG-005',
          assetId: 'AST-008',
          assetName: 'MacBook Pro 16"',
          assetType: 'Laptop',
          assignedTo: 'Emily Chen',
          assignedToId: 'EMP-005',
          assignedToEmail: 'emily.chen@company.com',
          assignedToPhone: '+1-555-0127',
          department: 'Design',
          assignedDate: '2024-01-22',
          expectedReturnDate: '2024-12-31',
          returnDate: null,
          status: 'Active',
          location: 'Design Studio, Workstation 5',
          purpose: 'Graphic Design Work',
          condition: 'Excellent',
          notes: 'High-performance machine for design work',
          assignedBy: 'Design Manager',
          lastUpdated: '2024-01-22T11:30:00Z'
        },
        {
          id: 'ASG-006',
          assetId: 'AST-009',
          assetName: 'Company Vehicle - Honda Civic',
          assetType: 'Vehicle',
          assignedTo: 'Michael Rodriguez',
          assignedToId: 'EMP-006',
          assignedToEmail: 'michael.rodriguez@company.com',
          assignedToPhone: '+1-555-0128',
          department: 'Sales',
          assignedDate: '2024-01-18',
          expectedReturnDate: '2024-12-31',
          returnDate: null,
          status: 'Active',
          location: 'Company Parking Lot, Space 15',
          purpose: 'Client Visits & Sales Calls',
          condition: 'Good',
          notes: 'Primary vehicle for sales team',
          assignedBy: 'Sales Manager',
          lastUpdated: '2024-01-18T08:00:00Z'
        }
      ]);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch assignment statistics
  const fetchAssignmentStats = async () => {
    try {
      const response = await AssignmentService.getAssignmentStats();
      return response.data;
    } catch (error) {
      console.error('Error fetching assignment stats:', error);
      return null;
    }
  };

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAssignments();
    success('Assignments data refreshed successfully');
    setRefreshing(false);
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/assignment-usage';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Handle search and filter changes
  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
  };

  const handleFilterChange = (newFilterStatus, newFilterDepartment) => {
    setFilterStatus(newFilterStatus);
    setFilterDepartment(newFilterDepartment);
  };

  // Export assignments data
  const handleExport = async (format = 'csv') => {
    try {
      const blob = await AssignmentService.exportAssignments(format, {
        search: searchTerm,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        department: filterDepartment !== 'all' ? filterDepartment : undefined
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assignments-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      success('Assignments data exported successfully');
    } catch (error) {
      showError('Failed to export assignments data');
    }
  };

  // Handle test execution
  const handleRunTests = async () => {
    if (!isDevelopmentMode) {
      showError('Tests can only be run in development mode');
      return;
    }

    setIsRunningTests(true);
    try {
      const results = await AssignmentTestService.runAllTests();
      setTestResults(results);
      setShowTestResults(true);

      if (results.success) {
        success(`All tests passed! (${results.passed}/${results.totalTests})`);
      } else {
        showError(`Tests completed with issues: ${results.failed} failed, ${results.errors} errors`);
      }
    } catch (error) {
      console.error('Test execution failed:', error);
      showError('Failed to run tests: ' + error.message);
    } finally {
      setIsRunningTests(false);
    }
  };

  // Test automation handlers with toast messages
  const handleAutoFill = async (scenario, formRef) => {
    try {
      const testData = await AssignmentTestAutomation.autoFillForm(scenario, formRef);
      success(`Form auto-filled with ${scenario} scenario data`);
    } catch (error) {
      showError('Auto-fill failed: ' + error.message);
    }
  };

  const handleCreateTest = async (scenario) => {
    try {
      const result = await AssignmentTestAutomation.createTestAssignment(scenario);
      if (result.success) {
        success(`Test assignment created successfully with ${scenario} scenario`);
        // Refresh the assignments list
        fetchAssignments();
      } else {
        showError(`Test assignment creation failed: ${result.error}`);
      }
    } catch (error) {
      showError('Create test failed: ' + error.message);
    }
  };

  const handleValidate = async () => {
    try {
      const validation = await AssignmentTestAutomation.validateForm(formData);
      if (validation.isValid) {
        success(`Form validation passed! Score: ${validation.score}/100`);
      } else {
        showError(`Form validation failed: ${validation.errors.length} errors found`);
      }
      console.log('Validation results:', validation);
    } catch (error) {
      showError('Validation failed: ' + error.message);
    }
  };

  const handleReset = (formRef) => {
    try {
      const defaultData = AssignmentTestAutomation.resetForm(formRef);
      setFormData(defaultData);
      setFormErrors({});
      setFormWarnings({});
      setTouchedFields({});
      success('Form reset to default state');
    } catch (error) {
      showError('Reset failed: ' + error.message);
    }
  };

  const handleDebug = (enabled) => {
    if (enabled) {
      AssignmentTestAutomation.enableDebugMode();
      success('Debug mode enabled - check console for detailed logs');
    } else {
      AssignmentTestAutomation.disableDebugMode();
      success('Debug mode disabled');
    }
  };

  const handleRunSuite = async () => {
    setIsRunningTests(true);
    try {
      const results = await AssignmentTestAutomation.runTestSuite();
      setAutomationTestResults(results);

      if (results.success) {
        success(`Test suite completed successfully! ${results.passed}/${results.totalTests} tests passed`);
      } else {
        showError(`Test suite completed with issues: ${results.failed} failed, ${results.errors} errors`);
      }
    } catch (error) {
      showError('Test suite failed: ' + error.message);
    } finally {
      setIsRunningTests(false);
    }
  };

  // History functions
  const addToHistory = (action, assignment, changes = [], notes = '') => {
    const historyRecord = {
      id: Date.now().toString(),
      action,
      assignmentId: assignment.id || assignment.assignmentId,
      assetId: assignment.assetId,
      assetName: assignment.assetName,
      assetType: assignment.assetType,
      assignedTo: assignment.assignedTo,
      assignedToId: assignment.assignedToId,
      department: assignment.department,
      timestamp: new Date().toISOString(),
      changes,
      notes,
      performedBy: 'Current User'
    };

    setAssignmentHistory(prev => [historyRecord, ...prev]);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'created':
        return <Users size={16} style={{ color: '#10B981' }} />;
      case 'updated':
        return <RefreshCw size={16} style={{ color: '#3B82F6' }} />;
      case 'returned':
        return <CheckCircle size={16} style={{ color: '#F59E0B' }} />;
      case 'deleted':
        return <XCircle size={16} style={{ color: '#EF4444' }} />;
      default:
        return <Clock size={16} style={{ color: '#6B7280' }} />;
    }
  };

  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'created':
        return 'bg-success';
      case 'updated':
        return 'bg-primary';
      case 'returned':
        return 'bg-warning';
      case 'deleted':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  const handleExportHistory = () => {
    const csvContent = [
      ['Timestamp', 'Action', 'Asset Name', 'Asset Type', 'Assigned To', 'Department', 'Notes'],
      ...filteredHistory.map(record => [
        new Date(record.timestamp).toLocaleString(),
        record.action,
        record.assetName,
        record.assetType,
        record.assignedTo,
        record.department,
        record.notes || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assignment-history-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    success('Assignment history exported successfully');
  };

  const handleRefreshHistory = async () => {
    try {
      await fetchAssignmentHistory();
      success('Assignment history refreshed');
    } catch (error) {
      showError('Failed to refresh assignment history');
    }
  };

  // Filtered history based on current filters
  const filteredHistory = assignmentHistory.filter(record => {
    // Date range filter
    if (historyFilters.dateRange !== 'all') {
      const recordDate = new Date(record.timestamp);
      const now = new Date();
      let daysAgo = 0;

      switch (historyFilters.dateRange) {
        case '7days':
          daysAgo = 7;
          break;
        case '30days':
          daysAgo = 30;
          break;
        case '90days':
          daysAgo = 90;
          break;
      }

      if (daysAgo > 0) {
        const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
        if (recordDate < cutoffDate) return false;
      }
    }

    // Action type filter
    if (historyFilters.actionType !== 'all' && record.action !== historyFilters.actionType) {
      return false;
    }

    // User filter
    if (historyFilters.user !== 'all' && record.assignedTo !== historyFilters.user) {
      return false;
    }

    // Department filter
    if (historyFilters.department !== 'all' && record.department !== historyFilters.department) {
      return false;
    }

    return true;
  });

  // Fetch assignment history from backend
  const fetchAssignmentHistory = async () => {
    try {
      const response = await AssignmentService.getAssignmentHistory(historyFilters);
      setAssignmentHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching assignment history:', error);
      // Fallback to empty array if API fails
      setAssignmentHistory([]);
    }
  };

  // Fetch analytics data
  const fetchAnalyticsData = async () => {
    try {
      setAnalyticsLoading(true);

      // Calculate analytics from current assignments data
      const departmentStats = calculateDepartmentStats(assignments);
      const assetTypeStats = calculateAssetTypeStats(assignments);
      const utilizationTrends = calculateUtilizationTrends(assignments);
      const topUsers = calculateTopUsers(assignments);
      const overdueAnalysis = calculateOverdueAnalysis(assignments);
      const monthlyTrends = calculateMonthlyTrends(assignments);

      setAnalyticsData({
        departmentStats,
        assetTypeStats,
        utilizationTrends,
        topUsers,
        overdueAnalysis,
        monthlyTrends
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Analytics calculation functions
  const calculateDepartmentStats = (assignments) => {
    const deptStats = {};
    assignments.forEach(assignment => {
      const dept = assignment.department || 'Unknown';
      if (!deptStats[dept]) {
        deptStats[dept] = { total: 0, active: 0, returned: 0, overdue: 0 };
      }
      deptStats[dept].total++;
      if (assignment.status === 'Active') deptStats[dept].active++;
      if (assignment.status === 'Returned') deptStats[dept].returned++;
      if (assignment.status === 'Overdue') deptStats[dept].overdue++;
    });

    return Object.entries(deptStats).map(([dept, stats]) => ({
      department: dept,
      ...stats,
      utilization: Math.round((stats.active / stats.total) * 100)
    }));
  };

  const calculateAssetTypeStats = (assignments) => {
    const typeStats = {};
    assignments.forEach(assignment => {
      const type = assignment.assetType || 'Unknown';
      if (!typeStats[type]) {
        typeStats[type] = { total: 0, active: 0, returned: 0, overdue: 0 };
      }
      typeStats[type].total++;
      if (assignment.status === 'Active') typeStats[type].active++;
      if (assignment.status === 'Returned') typeStats[type].returned++;
      if (assignment.status === 'Overdue') typeStats[type].overdue++;
    });

    return Object.entries(typeStats).map(([type, stats]) => ({
      assetType: type,
      ...stats,
      utilization: Math.round((stats.active / stats.total) * 100)
    }));
  };

  const calculateUtilizationTrends = (assignments) => {
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      const dayAssignments = assignments.filter(a =>
        new Date(a.assignedDate).toISOString().split('T')[0] <= dateStr
      );

      const active = dayAssignments.filter(a => a.status === 'Active').length;
      const total = dayAssignments.length;

      last7Days.push({
        date: dateStr,
        active,
        total,
        utilization: total > 0 ? Math.round((active / total) * 100) : 0
      });
    }
    return last7Days;
  };

  const calculateTopUsers = (assignments) => {
    const userStats = {};
    assignments.forEach(assignment => {
      const user = assignment.assignedTo || 'Unknown';
      if (!userStats[user]) {
        userStats[user] = { total: 0, active: 0, returned: 0, department: assignment.department };
      }
      userStats[user].total++;
      if (assignment.status === 'Active') userStats[user].active++;
      if (assignment.status === 'Returned') userStats[user].returned++;
    });

    return Object.entries(userStats)
      .map(([user, stats]) => ({ user, ...stats }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  };

  const calculateOverdueAnalysis = (assignments) => {
    const today = new Date();
    const overdue = assignments.filter(assignment => {
      if (assignment.status !== 'Active') return false;
      if (!assignment.expectedReturnDate) return false;
      return new Date(assignment.expectedReturnDate) < today;
    });

    return overdue.map(assignment => ({
      assignmentId: assignment.assignmentId,
      assetName: assignment.assetName,
      assignedTo: assignment.assignedTo,
      department: assignment.department,
      expectedReturnDate: assignment.expectedReturnDate,
      daysOverdue: Math.ceil((today - new Date(assignment.expectedReturnDate)) / (1000 * 60 * 60 * 24))
    }));
  };

  const calculateMonthlyTrends = (assignments) => {
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthStr = date.toISOString().substring(0, 7);

      const monthAssignments = assignments.filter(a =>
        new Date(a.assignedDate).toISOString().substring(0, 7) === monthStr
      );

      last6Months.push({
        month: monthStr,
        created: monthAssignments.length,
        active: monthAssignments.filter(a => a.status === 'Active').length,
        returned: monthAssignments.filter(a => a.status === 'Returned').length
      });
    }
    return last6Months;
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchAssignments();
    loadAssets();
    loadEmployees();
  }, [searchTerm, filterStatus, filterDepartment]);

  // Load history data when component mounts and when history filters change
  useEffect(() => {
    fetchAssignmentHistory();
  }, [historyFilters]);

  // Populate form data when edit modal opens
  useEffect(() => {
    if (showEditModal && selectedAssignment) {
      // Format dates for HTML date inputs (YYYY-MM-DD)
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return '';
          return date.toISOString().split('T')[0];
        } catch (error) {
          console.error('Error formatting date:', error);
          return '';
        }
      };

      // Helper to convert ID to string (handles both ObjectId and string)
      const getIdAsString = (id) => {
        if (!id) return '';
        if (typeof id === 'string') return id;
        if (id.toString) return id.toString();
        return String(id);
      };

      console.log('Populating form with assignment:', selectedAssignment);

      // Find matching asset by assetCode or _id
      const assetIdToUse = getIdAsString(selectedAssignment.assetId) || '';
      const matchingAsset = availableAssets.find(a =>
        a.assetCode === assetIdToUse ||
        a.id === assetIdToUse ||
        a._id === assetIdToUse ||
        a.assetCode === selectedAssignment.assetId ||
        a.id === selectedAssignment.assetId
      );
      const finalAssetId = matchingAsset ? (matchingAsset.id || matchingAsset.assetCode || matchingAsset._id) : assetIdToUse;

      // Find matching employee by _id
      const assignedToIdToUse = getIdAsString(selectedAssignment.assignedToId) || '';
      const matchingEmployee = employees.find(e =>
        e._id === assignedToIdToUse ||
        e.id === assignedToIdToUse ||
        e._id === selectedAssignment.assignedToId ||
        e.id === selectedAssignment.assignedToId
      );
      const finalAssignedToId = matchingEmployee ? (matchingEmployee.id || matchingEmployee._id) : assignedToIdToUse;

      const formDataToSet = {
        assetId: finalAssetId,
        assignedToId: finalAssignedToId,
        department: selectedAssignment.department || '',
        assignedDate: formatDateForInput(selectedAssignment.assignedDate),
        expectedReturnDate: formatDateForInput(selectedAssignment.expectedReturnDate),
        location: selectedAssignment.location || '',
        purpose: selectedAssignment.purpose || '',
        notes: selectedAssignment.notes || ''
      };

      console.log('Matching asset:', matchingAsset);
      console.log('Matching employee:', matchingEmployee);
      console.log('Final assetId:', finalAssetId);
      console.log('Final assignedToId:', finalAssignedToId);

      console.log('Setting formData to:', formDataToSet);
      setFormData(formDataToSet);
    } else if (!showEditModal) {
      // Reset form when modal closes
      setFormData({
        assetId: '',
        assignedToId: '',
        department: '',
        assignedDate: new Date().toISOString().split('T')[0],
        expectedReturnDate: '',
        location: '',
        purpose: '',
        notes: ''
      });
    }
  }, [showEditModal, selectedAssignment]);

  // Load analytics data when assignments change
  useEffect(() => {
    if (assignments.length > 0) {
      fetchAnalyticsData();
    }
  }, [assignments]);

  // Available assets loaded from backend
  const [availableAssets, setAvailableAssets] = useState([]);

  // Employees loaded from backend
  const [employees, setEmployees] = useState([]);

  // Load assets from backend
  const loadAssets = async () => {
    try {
      console.log('Loading assets for assignment form...');
      const response = await assetService.getAssets({
        status: 'Active',
        limit: 100 // API maximum limit is 100
      });

      console.log('Assets API response:', response);
      console.log('Response success:', response?.success);
      console.log('Response data:', response?.data);
      console.log('Response data.assets:', response?.data?.assets);

      // Handle different response structures
      let assetsArray = [];

      if (response?.success && response?.data?.assets) {
        assetsArray = response.data.assets;
      } else if (response?.data && Array.isArray(response.data)) {
        assetsArray = response.data;
      } else if (Array.isArray(response)) {
        assetsArray = response;
      } else if (response?.assets && Array.isArray(response.assets)) {
        assetsArray = response.assets;
      }

      console.log('Extracted assets array:', assetsArray);
      console.log('Number of assets:', assetsArray.length);

      if (assetsArray.length > 0) {
        // Transform assets to match the expected format
        const transformedAssets = assetsArray.map(asset => {
          const assetCode = asset.assetCode || asset.assetId || '';
          const assetId = assetCode || asset._id?.toString() || asset.id?.toString() || '';

          return {
            id: assetId,
            _id: asset._id?.toString() || asset.id?.toString() || '',
            assetCode: assetCode,
            assetId: asset.assetId || assetCode,
            name: asset.name || 'Unnamed Asset',
            type: asset.type || 'Other',
            category: asset.category || asset.type || 'Other',
            condition: asset.condition || asset.specifications?.condition || 'Good',
            location: asset.location?.address ||
              (asset.location?.building ? `${asset.location.building}${asset.location.floor ? `, Floor ${asset.location.floor}` : ''}${asset.location.room ? `, Room ${asset.location.room}` : ''}` : '') ||
              (typeof asset.location === 'string' ? asset.location : ''),
            value: asset.cost || asset.currentValue || asset.value || 0
          };
        });

        console.log('Transformed assets:', transformedAssets);
        console.log('Setting availableAssets with', transformedAssets.length, 'assets');
        setAvailableAssets(transformedAssets);
      } else {
        console.warn('No assets found in response. Response structure:', response);
        setAvailableAssets([]);
      }
    } catch (error) {
      console.error('Error loading assets:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        data: error.response?.data
      });
      setAvailableAssets([]);
    }
  };

  // Load employees/users from backend
  const loadEmployees = async () => {
    try {
      console.log('Loading employees for assignment form...');
      const response = await UserService.getUsers({
        limit: 100, // API maximum limit is 100
        page: 1
        // Removed status filter to get all users
      });

      console.log('Users API response:', response);
      console.log('Response success:', response?.success);
      console.log('Response data:', response?.data);
      console.log('Response data type:', typeof response?.data);
      console.log('Response data length:', Array.isArray(response?.data) ? response?.data.length : 'Not an array');

      // Handle different response structures
      let usersArray = [];

      if (response?.success && response?.data && Array.isArray(response.data)) {
        usersArray = response.data;
      } else if (response?.data && Array.isArray(response.data)) {
        usersArray = response.data;
      } else if (Array.isArray(response)) {
        usersArray = response;
      } else if (response?.users && Array.isArray(response.users)) {
        usersArray = response.users;
      }

      console.log('Extracted users array:', usersArray);
      console.log('Number of users:', usersArray.length);

      if (usersArray.length > 0) {
        // Transform users to match the expected format
        const transformedEmployees = usersArray.map(user => ({
          id: user._id?.toString() || user.id?.toString(),
          _id: user._id?.toString() || user.id?.toString(),
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.username || 'Unknown User',
          email: user.email || '',
          phone: user.phone || user.phoneNumber || '',
          department: user.department || '',
          position: user.position || user.role || ''
        }));

        console.log('Transformed employees:', transformedEmployees);
        console.log('Setting employees with', transformedEmployees.length, 'users');
        setEmployees(transformedEmployees);
      } else {
        console.warn('No users found in response. Response structure:', response);
        setEmployees([]);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        data: error.response?.data
      });
      setEmployees([]);
    }
  };

  // Form state for new assignment
  const [formData, setFormData] = useState({
    assetId: '',
    assignedToId: '',
    department: '',
    assignedDate: new Date().toISOString().split('T')[0],
    expectedReturnDate: '',
    location: '',
    purpose: '',
    notes: ''
  });

  // Calculate dynamic usage statistics from real data
  const usageStats = {
    totalAssignments: assignments.length,
    activeAssignments: assignments.filter(a => a.status === 'Active').length,
    returnedAssets: assignments.filter(a => a.status === 'Returned').length,
    utilizationRate: assignments.length > 0 ?
      Math.round((assignments.filter(a => a.status === 'Active').length / assignments.length) * 100) : 0
  };

  // Filtered assignments based on search and filters
  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = searchTerm === '' ||
      assignment.assetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.assignedTo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.id.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || assignment.status === filterStatus;
    const matchesDepartment = filterDepartment === 'all' || assignment.department === filterDepartment;

    return matchesSearch && matchesStatus && matchesDepartment;
  });

  // Define columns for DataTable
  const columns = [
    {
      key: 'id',
      label: 'Assignment ID',
      width: '120px',
      render: (value, row) => (
        <div className="d-flex align-items-center gap-2">
          <UserCheck size={16} style={{ color: 'var(--primary-color)' }} />
          <strong style={{ color: 'var(--text-primary)' }}>{value}</strong>
        </div>
      )
    },
    {
      key: 'assetName',
      label: 'Asset Name',
      width: '200px',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{value}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>ID: {row.assetId}</div>
        </div>
      )
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      width: '150px',
      render: (value, row) => (
        <div className="d-flex align-items-center gap-2">
          <Users size={14} style={{ color: 'var(--text-muted)' }} />
          <span style={{ color: 'var(--text-primary)' }}>{value}</span>
        </div>
      )
    },
    {
      key: 'department',
      label: 'Department',
      width: '120px',
      render: (value) => (
        <span className="badge" style={{
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '500'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'assignedDate',
      label: 'Assigned Date',
      width: '120px',
      render: (value) => (
        <div className="d-flex align-items-center gap-2">
          <Calendar size={14} style={{ color: 'var(--text-muted)' }} />
          <span style={{ color: 'var(--text-primary)' }}>{new Date(value).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '100px',
      type: 'badge',
      badgeClass: (value) => {
        const statusClasses = {
          'Active': 'badge-success',
          'Returned': 'badge-secondary',
          'Pending': 'badge-warning'
        };
        return statusClasses[value] || 'badge-secondary';
      }
    },
    {
      key: 'location',
      label: 'Location',
      width: '150px',
      render: (value) => (
        <div className="d-flex align-items-center gap-2">
          <MapPin size={14} style={{ color: 'var(--text-muted)' }} />
          <span style={{ color: 'var(--text-primary)' }}>{value}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '60px',
      render: (_, assignment) => (
        <div className="d-flex justify-content-center">
          <ActionDropdown
            assignment={assignment}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>
      )
    }
  ];

  // Handler functions
  const handleView = (assignment) => {
    setSelectedAssignment(assignment);
    setShowViewModal(true);
  };

  const handleEdit = (assignment) => {
    console.log('handleEdit called with assignment:', assignment);
    setSelectedAssignment(assignment);
    setShowEditModal(true);
    // Form data will be populated by useEffect when showEditModal becomes true
  };

  const handleReturn = (assignment) => {
    setSelectedAssignment(assignment);
    setShowReturnModal(true);
  };

  const handleDelete = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDeleteModal(true);
  };

  const handleAddNew = () => {
    navigate('/assets/assignment/create');
  };

  // Define action buttons for PageHeader (after handleAddNew is defined)
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={(isLoading || refreshing) ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh assignments data',
      disabled: isLoading || refreshing
    },
    {
      label: 'New Assignment',
      icon: <Plus size={18} />,
      onClick: handleAddNew,
      variant: 'primary',
      tooltip: 'Create a new assignment'
    }
  ];

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Update existing assignment via API
      const selectedEmployee = employees.find(e =>
        e._id === formData.assignedToId ||
        e.id === formData.assignedToId
      );

      const updateData = {
        assignedToId: selectedEmployee?._id || selectedEmployee?.id || formData.assignedToId,
        assignedTo: selectedEmployee?.name || selectedAssignment.assignedTo,
        assignedToEmail: selectedEmployee?.email || selectedAssignment.assignedToEmail,
        assignedToPhone: selectedEmployee?.phone || selectedAssignment.assignedToPhone,
        department: formData.department,
        assignedDate: formData.assignedDate,
        expectedReturnDate: formData.expectedReturnDate,
        location: formData.location,
        purpose: formData.purpose,
        notes: formData.notes
      };

      const response = await AssignmentService.updateAssignment(selectedAssignment.id, updateData);
      setAssignments(prev => prev.map(assignment =>
        assignment.id === selectedAssignment.id ? response.data : assignment
      ));
      success('Assignment updated successfully');

      // Reset form and validation state
      setFormData({
        assetId: '',
        assignedToId: '',
        department: '',
        assignedDate: '',
        expectedReturnDate: '',
        location: '',
        purpose: '',
        notes: ''
      });
      setFormErrors({});
      setFormWarnings({});
      setTouchedFields({});

      setShowEditModal(false);
      setSelectedAssignment(null);

      // Refresh assignments and history after successful operation
      await fetchAssignments();
      await fetchAssignmentHistory();
    } catch (error) {
      console.error('Error updating assignment:', error);
      showError('Failed to update assignment: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReturnConfirm = async () => {
    setIsLoading(true);
    try {
      const response = await AssignmentService.returnAssignment(selectedAssignment.id, {
        returnDate: new Date().toISOString().split('T')[0]
      });

      setAssignments(prev => prev.map(assignment =>
        assignment.id === selectedAssignment.id ? response.data : assignment
      ));

      setShowReturnModal(false);
      setSelectedAssignment(null);
      success('Asset returned successfully');

      // Refresh assignments and history
      await fetchAssignments();
      await fetchAssignmentHistory();
    } catch (error) {
      console.error('Error returning asset:', error);
      showError('Failed to return asset: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await AssignmentService.deleteAssignment(selectedAssignment.id);

      setAssignments(prev => prev.filter(assignment => assignment.id !== selectedAssignment.id));

      setShowDeleteModal(false);
      setSelectedAssignment(null);
      success('Assignment deleted successfully');

      // Refresh assignments and history
      await fetchAssignments();
      await fetchAssignmentHistory();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      showError('Failed to delete assignment: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Mark field as touched
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));

    // Validate field in real-time
    const validation = validateField(name, value, formData);
    setFormErrors(prev => ({
      ...prev,
      [name]: validation.errors
    }));
    setFormWarnings(prev => ({
      ...prev,
      [name]: validation.warnings
    }));
  };

  // Handle form blur (when user leaves a field)
  const handleFormBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));

    // Validate field
    const validation = validateField(name, value, formData);
    setFormErrors(prev => ({
      ...prev,
      [name]: validation.errors
    }));
    setFormWarnings(prev => ({
      ...prev,
      [name]: validation.warnings
    }));
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Assignment & Usage"
        description="Track asset assignments, check-in/check-out, and allocation history"
        actions={headerActions}
      />
      <div className="page-container">
        {/* Error Display */}
        {error && (
          <div className="alert alert-danger" role="alert" style={{ marginBottom: '16px' }}>
            <AlertTriangle size={16} className="me-2" />
            <strong>Error:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              style={{ float: 'right' }}
            ></button>
          </div>
        )}

        {/* Asset Assignments Table */}
        <DataTable
          data={filteredAssignments}
          columns={columns}
          title="Asset Assignments"
          searchable={true}
          sortable={true}
          exportable={true}
          pagination={true}
          pageSize={10}
          showActions={false}
          showCheckboxes={false}
          //onView={handleView}
          //onEdit={handleEdit}
          //onDelete={handleDelete}
          onExport={handleExport}
          loading={isLoading}
          searchPlaceholder="Search assignments by asset name, employee, or department..."
          emptyMessage="No assignments found. Click 'New Assignment' to get started."
          initialHiddenColumns={['id']}
        />

        {/* History tab removed - keeping only assignments table */}

        {/* History tab section removed */}
        {false && (
          <div className="page-tab-content">
            {/* History Filters */}
            <div className="card" style={{ marginBottom: '24px' }}>
              <div className="card-body">
                <div className="history-filters d-flex flex-wrap align-items-end gap-3">
                  {/* Date Range Filter */}
                  <div style={{ minWidth: '140px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      Date Range
                    </label>
                    <select
                      className="form-select"
                      value={historyFilters.dateRange}
                      onChange={(e) => setHistoryFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                        backgroundColor: 'white',
                        color: '#374151',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      <option value="all">All Time</option>
                      <option value="7days">Last 7 Days</option>
                      <option value="30days">Last 30 Days</option>
                      <option value="90days">Last 90 Days</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>

                  {/* Action Type Filter */}
                  <div style={{ minWidth: '120px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      Action Type
                    </label>
                    <select
                      className="form-select"
                      value={historyFilters.actionType}
                      onChange={(e) => setHistoryFilters(prev => ({ ...prev, actionType: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                        backgroundColor: 'white',
                        color: '#374151',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      <option value="all">All Actions</option>
                      <option value="created">Created</option>
                      <option value="updated">Updated</option>
                      <option value="returned">Returned</option>
                      <option value="deleted">Deleted</option>
                      <option value="status_changed">Status Changed</option>
                      <option value="condition_changed">Condition Changed</option>
                      <option value="extended">Extended</option>
                      <option value="transferred">Transferred</option>
                      <option value="auto_overdue">Auto Overdue</option>
                    </select>
                  </div>

                  {/* User Filter */}
                  <div style={{ minWidth: '120px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      User
                    </label>
                    <select
                      className="form-select"
                      value={historyFilters.user}
                      onChange={(e) => setHistoryFilters(prev => ({ ...prev, user: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                        backgroundColor: 'white',
                        color: '#374151',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      <option value="all">All Users</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.name}>{emp.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Department Filter */}
                  <div style={{ minWidth: '120px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      Department
                    </label>
                    <select
                      className="form-select"
                      value={historyFilters.department}
                      onChange={(e) => setHistoryFilters(prev => ({ ...prev, department: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                        backgroundColor: 'white',
                        color: '#374151',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      <option value="all">All Departments</option>
                      <option value="IT">IT</option>
                      <option value="Operations">Operations</option>
                      <option value="HR">HR</option>
                      <option value="Design">Design</option>
                      <option value="Sales">Sales</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Finance">Finance</option>
                    </select>
                  </div>

                  {/* Action Buttons and Record Counter */}
                  <div className="d-flex align-items-center gap-3">
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={handleExportHistory}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <Download size={14} style={{ marginRight: '4px' }} />
                        Export
                      </button>
                      <button
                        className="btn btn-outline-primary"
                        onClick={handleRefreshHistory}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <RefreshCw size={14} style={{ marginRight: '4px' }} />
                        Refresh
                      </button>
                    </div>

                    <span style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      whiteSpace: 'nowrap'
                    }}>
                      {filteredHistory.length} of {assignmentHistory.length} records
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* History Timeline */}
            <div className="card">
              <div className="card-body">
                <h5 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginBottom: '20px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}>
                  Assignment History Timeline
                </h5>
                {filteredHistory.length === 0 ? (
                  <div className="text-center py-5">
                    <History size={48} style={{ color: '#6b7280', marginBottom: '16px' }} />
                    <h6 style={{
                      color: '#6b7280',
                      fontSize: '16px',
                      fontWeight: '500',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                    }}>
                      No history records found
                    </h6>
                    <p style={{
                      color: '#9ca3af',
                      fontSize: '14px',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                    }}>
                      Try adjusting your filters or check back later.
                    </p>
                  </div>
                ) : (
                  <div className="timeline">
                    {filteredHistory.map((record, index) => (
                      <div key={index} className="timeline-item">
                        <div className="timeline-marker">
                          {getActionIcon(record.action)}
                        </div>
                        <div className="timeline-content">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#1f2937',
                                marginBottom: '8px',
                                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                              }}>
                                {record.action === 'created' && 'Assignment Created'}
                                {record.action === 'updated' && 'Assignment Updated'}
                                {record.action === 'returned' && 'Asset Returned'}
                                {record.action === 'deleted' && 'Assignment Deleted'}
                              </h6>
                              <p style={{
                                fontSize: '14px',
                                color: '#6b7280',
                                marginBottom: '4px',
                                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                              }}>
                                <strong style={{ color: '#374151' }}>{record.assetName}</strong> ({record.assetType}) assigned to <strong style={{ color: '#374151' }}>{record.assignedTo}</strong>
                              </p>
                              <small style={{
                                fontSize: '12px',
                                color: '#9ca3af',
                                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                              }}>
                                {record.department} • {new Date(record.timestamp).toLocaleString()}
                              </small>
                            </div>
                            <div className="text-end">
                              <span className={`badge ${getActionBadgeClass(record.action)}`} style={{
                                fontSize: '11px',
                                fontWeight: '600',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                              }}>
                                {record.action.toUpperCase()}
                              </span>
                            </div>
                          </div>
                          {record.changes && record.changes.length > 0 && (
                            <div style={{ marginTop: '12px' }}>
                              <small style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                fontWeight: '500',
                                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                              }}>
                                Changes:
                              </small>
                              <ul className="list-unstyled mt-1">
                                {record.changes.map((change, idx) => (
                                  <li key={idx} style={{
                                    fontSize: '12px',
                                    color: '#6b7280',
                                    marginBottom: '2px',
                                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                                  }}>
                                    <strong>{change.field}:</strong> {change.oldValue} → {change.newValue}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {record.notes && (
                            <div style={{ marginTop: '12px' }}>
                              <small style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                              }}>
                                Notes: {record.notes}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Analytics tab removed - keeping only assignments table */}

        {/* Analytics tab section removed */}
        {false && (
          <div className="page-tab-content">
            {analyticsLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading analytics...</span>
                </div>
                <p className="mt-3 text-muted">Loading analytics data...</p>
              </div>
            ) : (
              <>
                {/* Department Analytics */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card" style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                      <div className="card-header" style={{
                        backgroundColor: '#f8fafc',
                        borderBottom: '1px solid #e5e7eb',
                        borderRadius: '12px 12px 0 0'
                      }}>
                        <h5 className="card-title mb-0" style={{
                          color: '#1f2937',
                          fontSize: '18px',
                          fontWeight: '600',
                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                        }}>
                          Department Utilization
                        </h5>
                      </div>
                      <div className="card-body" style={{ padding: '20px' }}>
                        <div className="d-flex flex-wrap gap-3" style={{ paddingBottom: '10px' }}>
                          {analyticsData.departmentStats.map((dept, index) => (
                            <div key={index} style={{ minWidth: '180px', maxWidth: '220px', flex: '0 0 auto' }}>
                              <div style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '16px',
                                backgroundColor: 'white',
                                transition: 'all 0.2s ease-in-out'
                              }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h6 className="mb-0" style={{
                                    color: '#374151',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                                  }}>
                                    {dept.department}
                                  </h6>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}>
                                    <div style={{
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      backgroundColor: dept.utilization === 100 ? '#10b981' : dept.utilization >= 80 ? '#4678be' : dept.utilization >= 60 ? '#f59e0b' : '#ef4444'
                                    }}></div>
                                    <span style={{
                                      backgroundColor: dept.utilization === 100 ? '#10b981' : dept.utilization >= 80 ? '#4678be' : dept.utilization >= 60 ? '#f59e0b' : '#ef4444',
                                      color: 'white',
                                      padding: '4px 12px',
                                      borderRadius: '20px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                                    }}>
                                      {dept.utilization}%
                                    </span>
                                  </div>
                                </div>
                                <div className="progress mb-3" style={{ height: '8px', backgroundColor: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
                                  <div
                                    className="progress-bar"
                                    style={{
                                      width: `${dept.utilization}%`,
                                      backgroundColor: dept.utilization === 100 ? '#10b981' : dept.utilization >= 80 ? '#4678be' : dept.utilization >= 60 ? '#f59e0b' : '#ef4444',
                                      borderRadius: '4px',
                                      transition: 'width 0.3s ease-in-out',
                                      position: 'relative'
                                    }}
                                  >
                                    <div style={{
                                      position: 'absolute',
                                      top: '0',
                                      left: '0',
                                      right: '0',
                                      bottom: '0',
                                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
                                      animation: 'shimmer 2s infinite'
                                    }}></div>
                                  </div>
                                </div>
                                <div className="d-flex justify-content-between align-items-center" style={{ fontSize: '14px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{
                                      width: '6px',
                                      height: '6px',
                                      borderRadius: '50%',
                                      backgroundColor: '#10b981'
                                    }}></div>
                                    <span style={{ color: '#6b7280', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                                      Active: <strong style={{ color: '#374151' }}>{dept.active}</strong>
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{
                                      width: '6px',
                                      height: '6px',
                                      borderRadius: '50%',
                                      backgroundColor: '#6b7280'
                                    }}></div>
                                    <span style={{ color: '#6b7280', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                                      Total: <strong style={{ color: '#374151' }}>{dept.total}</strong>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Asset Type Analytics & Top Users */}
                <div className="row mb-5">
                  <div className="col-md-6">
                    <div className="card" style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      height: '100%'
                    }}>
                      <div className="card-header" style={{
                        backgroundColor: '#f8fafc',
                        borderBottom: '1px solid #e5e7eb',
                        borderRadius: '12px 12px 0 0'
                      }}>
                        <h5 className="card-title mb-0" style={{
                          color: '#1f2937',
                          fontSize: '16px',
                          fontWeight: '600',
                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                        }}>
                          Asset Type Distribution
                        </h5>
                      </div>
                      <div className="card-body" style={{ padding: '16px' }}>
                        <div className="d-flex flex-wrap gap-2" style={{ paddingBottom: '8px' }}>
                          {analyticsData.assetTypeStats.map((type, index) => (
                            <div key={index} style={{ minWidth: '130px', maxWidth: '160px', flex: '0 0 auto' }}>
                              <div style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '16px',
                                backgroundColor: 'white',
                                textAlign: 'center',
                                position: 'relative',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  position: 'absolute',
                                  top: '0',
                                  right: '0',
                                  width: '40px',
                                  height: '40px',
                                  background: `conic-gradient(${type.utilization === 100 ? '#10b981' : type.utilization >= 80 ? '#4678be' : type.utilization >= 60 ? '#f59e0b' : '#ef4444'} ${type.utilization * 3.6}deg, #f3f4f6 0deg)`,
                                  borderRadius: '0 8px 0 8px',
                                  opacity: '0.1'
                                }}></div>
                                <div style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  color: '#374151',
                                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                                  marginBottom: '12px',
                                  position: 'relative',
                                  zIndex: '1'
                                }}>
                                  {type.assetType}
                                </div>
                                <div style={{
                                  fontSize: '24px',
                                  fontWeight: '700',
                                  color: type.utilization === 100 ? '#10b981' : type.utilization >= 80 ? '#4678be' : type.utilization >= 60 ? '#f59e0b' : '#ef4444',
                                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                                  marginBottom: '8px',
                                  position: 'relative',
                                  zIndex: '1'
                                }}>
                                  {type.utilization}%
                                </div>
                                <div className="progress mb-3" style={{ height: '6px', backgroundColor: '#f3f4f6', borderRadius: '3px', overflow: 'hidden' }}>
                                  <div
                                    className="progress-bar"
                                    style={{
                                      width: `${type.utilization}%`,
                                      backgroundColor: type.utilization === 100 ? '#10b981' : type.utilization >= 80 ? '#4678be' : type.utilization >= 60 ? '#f59e0b' : '#ef4444',
                                      borderRadius: '3px',
                                      transition: 'width 0.3s ease-in-out'
                                    }}
                                  ></div>
                                </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: '#6b7280',
                                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  <div style={{
                                    width: '4px',
                                    height: '4px',
                                    borderRadius: '50%',
                                    backgroundColor: '#10b981'
                                  }}></div>
                                  <span>{type.active}/{type.total}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Top Users */}
                  <div className="col-md-6">
                    <div className="card" style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      height: '100%'
                    }}>
                      <div className="card-header" style={{
                        backgroundColor: '#f8fafc',
                        borderBottom: '1px solid #e5e7eb',
                        borderRadius: '12px 12px 0 0'
                      }}>
                        <h5 className="card-title mb-0" style={{
                          color: '#1f2937',
                          fontSize: '16px',
                          fontWeight: '600',
                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                        }}>
                          Top Users by Assignments
                        </h5>
                      </div>
                      <div className="card-body" style={{ padding: '16px' }}>
                        <div className="d-flex flex-wrap gap-2" style={{ paddingBottom: '8px' }}>
                          {analyticsData.topUsers.map((user, index) => (
                            <div key={index} style={{ minWidth: '110px', maxWidth: '140px', flex: '0 0 auto' }}>
                              <div style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '14px',
                                backgroundColor: 'white',
                                textAlign: 'center',
                                position: 'relative',
                                transition: 'all 0.2s ease-in-out'
                              }}>
                                <div style={{
                                  position: 'absolute',
                                  top: '8px',
                                  left: '8px',
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  backgroundColor: index === 0 ? '#fbbf24' : index === 1 ? '#9ca3af' : index === 2 ? '#f59e0b' : '#e5e7eb'
                                }}></div>
                                <div style={{
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  color: '#374151',
                                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                                  marginBottom: '8px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  paddingTop: '4px'
                                }}>
                                  {user.user}
                                </div>
                                <div style={{
                                  fontSize: '20px',
                                  fontWeight: '700',
                                  color: '#4678be',
                                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                                  marginBottom: '6px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '4px'
                                }}>
                                  <span>{user.total}</span>
                                  {index < 3 && (
                                    <div style={{
                                      fontSize: '10px',
                                      color: index === 0 ? '#fbbf24' : index === 1 ? '#9ca3af' : '#f59e0b',
                                      fontWeight: '600'
                                    }}>
                                      #{index + 1}
                                    </div>
                                  )}
                                </div>
                                <div style={{
                                  fontSize: '10px',
                                  color: '#6b7280',
                                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '4px'
                                }}>
                                  <div style={{
                                    width: '3px',
                                    height: '3px',
                                    borderRadius: '50%',
                                    backgroundColor: '#6b7280'
                                  }}></div>
                                  <span>{user.department}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Utilization Trends */}
                <div className="row mb-5">
                  <div className="col-12">
                    <div className="card" style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                      <div className="card-header" style={{
                        backgroundColor: '#f8fafc',
                        borderBottom: '1px solid #e5e7eb',
                        borderRadius: '12px 12px 0 0'
                      }}>
                        <h5 className="card-title mb-0" style={{
                          color: '#1f2937',
                          fontSize: '16px',
                          fontWeight: '600',
                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                        }}>
                          7-Day Utilization Trend
                        </h5>
                      </div>
                      <div className="card-body" style={{ padding: '20px' }}>
                        <div className="d-flex justify-content-between align-items-end" style={{ height: '120px' }}>
                          {analyticsData.utilizationTrends.map((day, index) => (
                            <div key={index} className="flex-fill text-center" style={{ minWidth: '60px' }}>
                              <div style={{
                                height: `${Math.max(day.utilization * 0.8, 8)}px`,
                                backgroundColor: day.utilization >= 80 ? '#10b981' : day.utilization >= 60 ? '#4678be' : day.utilization >= 40 ? '#f59e0b' : '#ef4444',
                                borderRadius: '4px 4px 0 0',
                                marginBottom: '8px',
                                transition: 'height 0.3s ease-in-out',
                                minHeight: '8px'
                              }}></div>
                              <div style={{
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#374151',
                                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                                marginBottom: '4px'
                              }}>
                                {day.utilization}%
                              </div>
                              <div style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                              }}>
                                {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Overdue Analysis */}
                {analyticsData.overdueAnalysis.length > 0 && (
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card border-warning">
                        <div className="card-header bg-warning text-dark">
                          <h5 className="card-title mb-0">
                            <AlertTriangle size={20} className="me-2" />
                            Overdue Assignments ({analyticsData.overdueAnalysis.length})
                          </h5>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th>Assignment ID</th>
                                  <th>Asset</th>
                                  <th>Assigned To</th>
                                  <th>Department</th>
                                  <th>Expected Return</th>
                                  <th>Days Overdue</th>
                                </tr>
                              </thead>
                              <tbody>
                                {analyticsData.overdueAnalysis.map((item, index) => (
                                  <tr key={index}>
                                    <td>{item.assignmentId}</td>
                                    <td>{item.assetName}</td>
                                    <td>{item.assignedTo}</td>
                                    <td>{item.department}</td>
                                    <td>{new Date(item.expectedReturnDate).toLocaleDateString()}</td>
                                    <td>
                                      <span className="badge bg-danger">{item.daysOverdue} days</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Monthly Trends */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card" style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                      <div className="card-header" style={{
                        backgroundColor: '#f8fafc',
                        borderBottom: '1px solid #e5e7eb',
                        borderRadius: '12px 12px 0 0'
                      }}>
                        <h5 className="card-title mb-0" style={{
                          color: '#1f2937',
                          fontSize: '16px',
                          fontWeight: '600',
                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                        }}>
                          6-Month Assignment Trends
                        </h5>
                      </div>
                      <div className="card-body" style={{ padding: '20px' }}>
                        <div className="d-flex flex-wrap gap-2" style={{ paddingBottom: '10px' }}>
                          {analyticsData.monthlyTrends.map((month, index) => (
                            <div key={index} className="text-center" style={{ minWidth: '80px', maxWidth: '100px', flex: '0 0 auto' }}>
                              <div style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '12px',
                                backgroundColor: 'white',
                                margin: '0 4px'
                              }}>
                                <div style={{
                                  fontSize: '18px',
                                  fontWeight: '700',
                                  color: '#4678be',
                                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                                  marginBottom: '8px'
                                }}>
                                  {month.created}
                                </div>
                                <div style={{
                                  fontSize: '11px',
                                  color: '#6b7280',
                                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                                  marginBottom: '8px'
                                }}>
                                  Created
                                </div>
                                <div className="d-flex justify-content-between" style={{ fontSize: '10px', marginBottom: '6px' }}>
                                  <span style={{ color: '#10b981', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                                    A: {month.active}
                                  </span>
                                  <span style={{ color: '#6b7280', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
                                    R: {month.returned}
                                  </span>
                                </div>
                                <div style={{
                                  fontSize: '10px',
                                  color: '#9ca3af',
                                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                                }}>
                                  {new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Add/Edit Assignment Modal */}
        {showEditModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '0',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 24px',
                backgroundColor: 'var(--primary-color)',
                color: 'white'
              }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: 'white',
                  margin: 0,
                  fontFamily: 'var(--font-family)'
                }}>
                  {showEditModal ? 'Edit Assignment' : 'Assignment'}
                </h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'white'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <div style={{ padding: '24px' }}>

                <form ref={formRef} onSubmit={handleFormSubmit} key={selectedAssignment?._id || selectedAssignment?.id || 'new'}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '16px',
                    marginBottom: '24px'
                  }}>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Asset
                      </label>
                      <select
                        name="assetId"
                        value={formData.assetId}
                        onChange={handleFormChange}
                        onBlur={handleFormBlur}
                        required
                        disabled={showEditModal}
                        className={getValidationClass('assetId', formData, touchedFields)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: formErrors.assetId && touchedFields.assetId ? '1px solid #ef4444' : '1px solid #d1d5db',
                          borderRadius: '0',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family)',
                          backgroundColor: showEditModal ? '#f9f9f9' : 'white',
                          cursor: showEditModal ? 'not-allowed' : 'default'
                        }}
                      >
                        <option value="">Select Asset</option>
                        {availableAssets && availableAssets.length > 0 ? (
                          availableAssets.map(asset => {
                            const assetValue = asset.assetCode || asset.assetId || asset.id || asset._id || '';
                            const assetKey = asset._id || asset.id || asset.assetCode || asset.assetId || Math.random();
                            return (
                              <option key={assetKey} value={assetValue}>
                                {asset.name || 'Unnamed Asset'} ({asset.type || 'Other'}) - {asset.condition || 'Good'}
                              </option>
                            );
                          })
                        ) : (
                          <option value="" disabled>No assets available</option>
                        )}
                      </select>
                      {formErrors.assetId && touchedFields.assetId && (
                        <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                          {formErrors.assetId[0]}
                        </div>
                      )}
                      {formWarnings.assetId && touchedFields.assetId && (
                        <div style={{ color: '#f59e0b', fontSize: '12px', marginTop: '4px' }}>
                          {formWarnings.assetId[0]}
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Assign to Employee
                      </label>
                      <select
                        name="assignedToId"
                        value={formData.assignedToId}
                        onChange={handleFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '0',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family)'
                        }}
                      >
                        <option value="">Select Employee</option>
                        {employees.map(employee => (
                          <option key={employee.id || employee._id} value={employee._id || employee.id}>
                            {employee.name} - {employee.department || 'N/A'}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Department
                      </label>
                      <select
                        name="department"
                        value={formData.department}
                        onChange={handleFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '0',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family)'
                        }}
                      >
                        <option value="">Select Department</option>
                        {(() => {
                          // Get unique departments from employees and assignments
                          const allDepartments = new Set();
                          employees.forEach(emp => {
                            if (emp.department) allDepartments.add(emp.department);
                          });
                          assignments.forEach(assign => {
                            if (assign.department) allDepartments.add(assign.department);
                          });
                          // Add common departments
                          ['IT', 'Operations', 'HR', 'Design', 'Sales', 'Marketing', 'Finance', 'Support', 'Legal', 'Procurement', 'Administration'].forEach(dept => allDepartments.add(dept));
                          // Sort and create options
                          return Array.from(allDepartments).sort().map(dept => (
                            <option key={dept} value={dept}>{dept}</option>
                          ));
                        })()}
                      </select>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Assignment Date
                      </label>
                      <input
                        type="date"
                        name="assignedDate"
                        value={formData.assignedDate}
                        onChange={handleFormChange}
                        required
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '0',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family)'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Expected Return Date
                      </label>
                      <input
                        type="date"
                        name="expectedReturnDate"
                        value={formData.expectedReturnDate}
                        onChange={handleFormChange}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '0',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family)'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Location
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleFormChange}
                        placeholder="e.g., Office Floor 2, Desk 15"
                        required
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '0',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family)'
                        }}
                      />
                    </div>

                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Purpose
                      </label>
                      <input
                        type="text"
                        name="purpose"
                        value={formData.purpose}
                        onChange={handleFormChange}
                        placeholder="e.g., Development Work, Client Visits"
                        required
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '0',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family)'
                        }}
                      />
                    </div>

                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px'
                      }}>
                        Notes
                      </label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleFormChange}
                        placeholder="Additional notes or comments..."
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '0',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family)',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="btn btn-outline"
                      style={{
                        borderRadius: '0'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="btn btn-primary"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        borderRadius: '0',
                        opacity: isLoading ? 0.6 : 1
                      }}
                    >
                      {isLoading ? (
                        <>
                          <RefreshCw size={16} className="animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={16} />
                          Update Assignment
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Assignment Modal */}
        {showViewModal && selectedAssignment && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '0',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 24px',
                backgroundColor: 'var(--primary-color)',
                color: 'white'
              }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: 'white',
                  margin: 0,
                  fontFamily: 'var(--font-family)'
                }}>
                  Assignment Details
                </h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'white'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <div style={{ padding: '24px' }}>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '16px'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                      Assignment ID
                    </label>
                    <div style={{
                      fontSize: '14px',
                      color: '#1f2937',
                      fontWeight: '500'
                    }}>
                      {selectedAssignment.id}
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                      Asset
                    </label>
                    <div style={{
                      fontSize: '14px',
                      color: '#1f2937',
                      fontWeight: '500'
                    }}>
                      {selectedAssignment.assetName} ({selectedAssignment.assetType})
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                      Assigned To
                    </label>
                    <div style={{
                      fontSize: '14px',
                      color: '#1f2937',
                      fontWeight: '500'
                    }}>
                      {selectedAssignment.assignedTo}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280'
                    }}>
                      {selectedAssignment.assignedToEmail}
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                      Department
                    </label>
                    <div style={{
                      fontSize: '14px',
                      color: '#1f2937',
                      fontWeight: '500'
                    }}>
                      {selectedAssignment.department}
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                      Status
                    </label>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      borderRadius: '0',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: selectedAssignment.status === 'Active' ? '#dcfce7' : '#f3f4f6',
                      color: selectedAssignment.status === 'Active' ? '#166534' : '#374151'
                    }}>
                      {selectedAssignment.status}
                    </span>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                      Assigned Date
                    </label>
                    <div style={{
                      fontSize: '14px',
                      color: '#1f2937'
                    }}>
                      {new Date(selectedAssignment.assignedDate).toLocaleDateString()}
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                      Location
                    </label>
                    <div style={{
                      fontSize: '14px',
                      color: '#1f2937'
                    }}>
                      {selectedAssignment.location}
                    </div>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      marginBottom: '4px'
                    }}>
                      Purpose
                    </label>
                    <div style={{
                      fontSize: '14px',
                      color: '#1f2937'
                    }}>
                      {selectedAssignment.purpose}
                    </div>
                  </div>

                  {selectedAssignment.notes && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#6b7280',
                        marginBottom: '4px'
                      }}>
                        Notes
                      </label>
                      <div style={{
                        fontSize: '14px',
                        color: '#1f2937',
                        backgroundColor: '#f9fafb',
                        padding: '12px',
                        borderRadius: '0',
                        border: '1px solid #e5e7eb'
                      }}>
                        {selectedAssignment.notes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Return Asset Modal */}
        <ConfirmationModal
          isOpen={showReturnModal}
          onClose={() => setShowReturnModal(false)}
          onConfirm={handleReturnConfirm}
          title="Return Asset"
          message={`Are you sure you want to return ${selectedAssignment?.assetName} from ${selectedAssignment?.assignedTo}?`}
          confirmText="Return Asset"
          cancelText="Cancel"
          type="warning"
          isLoading={isLoading}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Assignment"
          message={`Are you sure you want to delete assignment ${selectedAssignment?.id}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        {/* Toast Container */}
        <ToastContainer toasts={toasts} removeToast={removeToast} />

        {/* Test Results Modal */}
        <TestResultsModal
          isOpen={showTestResults}
          onClose={() => setShowTestResults(false)}
          testResults={testResults}
        />

        {/* Test Control Panel */}
        <TestControlPanel
          isOpen={showTestControlPanel}
          onClose={() => setShowTestControlPanel(false)}
          onAutoFill={handleAutoFill}
          onCreateTest={handleCreateTest}
          onValidate={handleValidate}
          onReset={handleReset}
          onDebug={handleDebug}
          onRunSuite={handleRunSuite}
          isRunning={isRunningTests}
          testResults={automationTestResults}
          formRef={formRef}
        />
      </div>
    </>
  );
};

export default AssignmentUsage;
